import React, { useEffect, useState, useMemo } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { heroSlides } from '../mock';

const SLIDE_DURATION = 5000; // ms

const Hero = () => {
  const [index, setIndex] = useState(0);

  // Auto-advance (continuous, never pauses)
  useEffect(() => {
    const t = setInterval(
      () => setIndex((i) => (i + 1) % heroSlides.length),
      SLIDE_DURATION
    );
    return () => clearInterval(t);
  }, []);

  // Preload next image
  useEffect(() => {
    const next = (index + 1) % heroSlides.length;
    const img = new Image();
    img.src = heroSlides[next].image;
  }, [index]);

  const current = heroSlides[index];

  // Generate stable random positions for gold particles
  const particles = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 6,
        duration: 8 + Math.random() * 10,
        size: 1 + Math.random() * 2.5,
        opacity: 0.25 + Math.random() * 0.5,
      })),
    []
  );

  return (
    <section
      id="home"
      className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a]"
    >
      {/* SLIDESHOW LAYERS */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.image}
            className={`absolute inset-0 transition-opacity duration-[1800ms] ease-in-out ${
              i === index ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title1}
              className={`w-full h-full object-cover ${slide.objectPositionClass || 'object-center'} ${
                i === index ? 'animate-[heroKenBurns_9s_ease-out_forwards]' : ''
              }`}
            />
          </div>
        ))}

        {/* Cinematic gradients */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/30 to-black/85 z-[2]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50 z-[2]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(0,0,0,0.55)_85%)] z-[2]" />

        {/* Moving ambient gold glow orbs */}
        <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
          <div className="absolute w-[55vw] h-[55vw] rounded-full opacity-[0.22] blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(232,213,168,0.9) 0%, rgba(201,169,110,0.35) 40%, transparent 70%)',
              animation: 'orbDriftA 22s ease-in-out infinite',
              mixBlendMode: 'screen',
            }}
          />
          <div className="absolute w-[45vw] h-[45vw] rounded-full opacity-[0.18] blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(255,200,140,0.9) 0%, rgba(180,120,60,0.3) 45%, transparent 75%)',
              animation: 'orbDriftB 28s ease-in-out infinite',
              mixBlendMode: 'screen',
            }}
          />
          <div className="absolute w-[38vw] h-[38vw] rounded-full opacity-[0.15] blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(201,169,110,0.95) 0%, rgba(232,213,168,0.25) 50%, transparent 80%)',
              animation: 'orbDriftC 18s ease-in-out infinite',
              mixBlendMode: 'screen',
            }}
          />
          <div className="absolute w-[30vw] h-[30vw] rounded-full opacity-[0.14] blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(255,220,170,0.85) 0%, transparent 70%)',
              animation: 'orbDriftD 32s ease-in-out infinite',
              mixBlendMode: 'screen',
            }}
          />
        </div>

        {/* Floating gold particles */}
        <div className="absolute inset-0 z-[3] pointer-events-none overflow-hidden">
          {particles.map((p) => (
            <span
              key={p.id}
              className="absolute rounded-full bg-[#e8d5a8]"
              style={{
                left: `${p.left}%`,
                bottom: '-20px',
                width: `${p.size}px`,
                height: `${p.size}px`,
                opacity: p.opacity,
                animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`,
                boxShadow: '0 0 6px rgba(232, 213, 168, 0.7)',
              }}
            />
          ))}
        </div>

        {/* Cinematic letterbox */}
        <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-[#0a0a0a] to-transparent z-[4]" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent z-[4]" />
      </div>

      {/* Gold corner ornaments */}
      <span className="hidden md:block absolute top-32 left-10 w-16 h-16 border-t border-l border-[#c9a96e]/60 z-[5]" />
      <span className="hidden md:block absolute top-32 right-10 w-16 h-16 border-t border-r border-[#c9a96e]/60 z-[5]" />
      <span className="hidden md:block absolute bottom-32 left-10 w-16 h-16 border-b border-l border-[#c9a96e]/60 z-[5]" />
      <span className="hidden md:block absolute bottom-32 right-10 w-16 h-16 border-b border-r border-[#c9a96e]/60 z-[5]" />

      {/* CONTENT */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center pt-24 pb-36 md:pt-28 md:pb-28">
        <div key={index} className="flex flex-col items-center w-full max-w-5xl">
          {/* Eyebrow with gold accents */}
          <div className="flex items-center gap-4 mb-6 md:mb-8 anim-fade-up" style={{ animationDelay: '0.1s' }}>
            <span className="h-px w-8 md:w-10 bg-[#c9a96e]/60" />
            <p className="text-[#c9a96e] text-[10px] md:text-xs tracking-[0.4em] md:tracking-[0.5em] uppercase whitespace-nowrap">
              {current.eyebrow}
            </p>
            <span className="h-px w-8 md:w-10 bg-[#c9a96e]/60" />
          </div>

          <h1
            className="font-serif text-white text-[10vw] sm:text-[9vw] md:text-[8.5vw] lg:text-[7vw] leading-[1.05] tracking-tight anim-fade-up px-2"
            style={{ animationDelay: '0.25s' }}
          >
            {current.title1}
          </h1>
          <h1
            className="font-serif italic text-[#e8c98a] text-[10vw] sm:text-[9vw] md:text-[8.5vw] lg:text-[7vw] leading-[1.05] tracking-tight -mt-1 anim-fade-up px-2"
            style={{ animationDelay: '0.4s', fontFamily: 'Cormorant Garamond, serif' }}
          >
            {current.title2}
          </h1>

          <p
            className="mt-6 md:mt-10 max-w-xl text-white/85 text-sm md:text-lg leading-relaxed font-light anim-fade-up px-2"
            style={{ animationDelay: '0.55s' }}
          >
            {current.caption}
          </p>
        </div>

        <div className="mt-8 md:mt-12 flex flex-col sm:flex-row gap-4 anim-fade-up" style={{ animationDelay: '0.75s' }}>
          <a
            href="#contact"
            className="group relative overflow-hidden bg-[#e8d5a8] text-black px-9 py-4 text-[11px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-3"
          >
            <span className="absolute inset-0 bg-[#c9a96e] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative z-10">Begin Your Story</span>
            <ArrowRight size={14} className="relative z-10 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#portfolio"
            className="border border-white/40 hover:border-[#c9a96e] hover:text-[#c9a96e] text-white px-9 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors duration-300 backdrop-blur-[2px] text-center"
          >
            View Portfolio
          </a>
        </div>

        {/* Slide progress / indicators */}
        <div className="absolute bottom-6 md:bottom-8 left-0 right-0 flex flex-col items-center gap-2 md:gap-3 z-10 pointer-events-none">
          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center px-4 pointer-events-auto">
            {heroSlides.map((s, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className="group relative h-px overflow-hidden"
                style={{ width: i === index ? '44px' : '18px', transition: 'width 0.6s ease' }}
                aria-label={`Slide ${i + 1}`}
              >
                <span className="absolute inset-0 bg-white/25" />
                {i === index && (
                  <span
                    key={`bar-${index}`}
                    className="absolute inset-0 bg-[#c9a96e] origin-left"
                    style={{
                      animation: `progressBar ${SLIDE_DURATION}ms linear forwards`,
                    }}
                  />
                )}
              </button>
            ))}
          </div>
          <p className="text-[#c9a96e]/70 text-[9px] tracking-[0.4em] uppercase">
            {String(index + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
          </p>
        </div>
      </div>

      <a
        href="#about"
        className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-white/70 hover:text-[#c9a96e] transition-colors"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">Discover</span>
        <ChevronDown size={16} className="animate-bounce" />
      </a>

      <style>{`
        @keyframes heroKenBurns {
          from { transform: scale(1.04) translate(0, 0); }
          to   { transform: scale(1.16) translate(-1.5%, -1%); }
        }
        @keyframes floatUp {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-110vh) translateX(20px); opacity: 0; }
        }
        @keyframes progressBar {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes orbDriftA {
          0%   { top: -20%; left: -15%; transform: scale(1); }
          25%  { top: 10%;  left: 30%;  transform: scale(1.15); }
          50%  { top: 40%;  left: 60%;  transform: scale(1); }
          75%  { top: 15%;  left: 25%;  transform: scale(1.2); }
          100% { top: -20%; left: -15%; transform: scale(1); }
        }
        @keyframes orbDriftB {
          0%   { top: 60%;  right: -20%; transform: scale(1); }
          30%  { top: 30%;  right: 10%;  transform: scale(1.1); }
          60%  { top: 5%;   right: 35%;  transform: scale(0.95); }
          100% { top: 60%;  right: -20%; transform: scale(1); }
        }
        @keyframes orbDriftC {
          0%   { bottom: -20%; left: 35%; transform: scale(1); }
          33%  { bottom: 25%;  left: 55%; transform: scale(1.25); }
          66%  { bottom: 50%;  left: 10%; transform: scale(0.9); }
          100% { bottom: -20%; left: 35%; transform: scale(1); }
        }
        @keyframes orbDriftD {
          0%   { top: 50%;  left: 70%; transform: scale(1); }
          40%  { top: 20%;  left: 5%;  transform: scale(1.3); }
          80%  { top: 70%;  left: 40%; transform: scale(0.85); }
          100% { top: 50%;  left: 70%; transform: scale(1); }
        }
        .anim-fade-up {
          opacity: 0;
          transform: translateY(28px);
          animation: heroFadeUp 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes heroFadeUp {
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
